export type SmsTemplate = {
  id: string;
  name: string;
  previewText?: string;
};

type SchemaProperty = {
  enum?: unknown[];
  enumNames?: unknown[];
  oneOf?: Array<{ const?: unknown; title?: string }>;
  default?: unknown;
  title?: string;
};

type PrepareStepParams = Record<string, { reactJsonSchemaForm?: { schema?: { properties?: Record<string, SchemaProperty> } } }> | null | undefined;

const NO_TEMPLATES_SENTINEL_PATTERNS = [/^--\s*no available templates?\s*--$/i, /^no available templates?$/i];
const TITLE_WITH_ID_PATTERN = /^(.+?)\s*\(([^)]+)\)\s*$/;

export const isNoAvailableTemplatesSentinel = (value: string): boolean => {
  const normalized = value.trim();
  if (!normalized) {
    return true;
  }
  return NO_TEMPLATES_SENTINEL_PATTERNS.some((pattern) => pattern.test(normalized));
};

/** Parse `"Name (tpl-xxx)"` → `{ name, id }`. */
export const parseTitleWithId = (title: string): { name: string; id?: string } => {
  const trimmed = title.trim();
  const match = TITLE_WITH_ID_PATTERN.exec(trimmed);
  if (!match) {
    return { name: trimmed };
  }
  return { name: match[1].trim(), id: match[2].trim() };
};

const extractFromTemplateProperty = (property: SchemaProperty | undefined): SmsTemplate[] => {
  if (!property) {
    return [];
  }

  if (Array.isArray(property.oneOf) && property.oneOf.length > 0) {
    return property.oneOf
      .map((option) => ({
        id: String(option.const ?? ''),
        name: String(option.title ?? option.const ?? ''),
      }))
      .filter((option) => option.id && !isNoAvailableTemplatesSentinel(option.id));
  }

  const enumValues = (property.enum ?? []).map(String).filter(Boolean);
  const enumNames = (property.enumNames ?? enumValues).map(String);

  return enumValues
    .map((id, index) => ({
      id,
      name: enumNames[index] ?? id,
    }))
    .filter((option) => !isNoAvailableTemplatesSentinel(option.id));
};

const extractFromArrayDefault = (value: unknown): SmsTemplate[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (typeof item === 'string') {
        return { id: item, name: item };
      }
      if (!item || typeof item !== 'object') {
        return null;
      }
      const record = item as Record<string, unknown>;
      const id = String(record.id ?? record.template ?? record.template_id ?? record.name ?? '');
      const name = String(record.name ?? record.template_name ?? record.title ?? id);
      const previewText = typeof record.template_text === 'string' ? record.template_text : typeof record.text === 'string' ? record.text : undefined;
      if (!id || isNoAvailableTemplatesSentinel(id)) {
        return null;
      }
      return { id, name: name || id, previewText };
    })
    .filter((item): item is SmsTemplate => item !== null);
};

/**
 * Template List shape: each `template_N` property is a readonly string with
 * `title: "name (tpl-id)"` and `default` as the message body.
 */
const extractFromStringTemplateProperty = (key: string, property: SchemaProperty): SmsTemplate | null => {
  if (typeof property.default !== 'string') {
    return null;
  }

  const previewText = property.default;
  const title = typeof property.title === 'string' ? property.title.trim() : '';
  const parsed = title ? parseTitleWithId(title) : { name: key };
  const id = parsed.id || key;
  const name = parsed.name || key;

  if (isNoAvailableTemplatesSentinel(id) || isNoAvailableTemplatesSentinel(name)) {
    return null;
  }

  return { id, name, previewText };
};

/** Extract SMS templates from prepare-step params (Template List or Edit/Delete Template). */
export const extractSmsTemplatesFromPrepareParams = (prepareParams: PrepareStepParams): SmsTemplate[] => {
  if (!prepareParams) {
    return [];
  }

  const stepKeys = Object.keys(prepareParams);
  const candidates: SmsTemplate[] = [];

  for (const stepKey of stepKeys) {
    const properties = prepareParams[stepKey]?.reactJsonSchemaForm?.schema?.properties ?? {};

    if (properties.template) {
      candidates.push(...extractFromTemplateProperty(properties.template));
    }

    for (const [key, property] of Object.entries(properties)) {
      if (key === 'template' || !property || !/template/i.test(key)) {
        continue;
      }

      if (typeof property.default === 'string') {
        const template = extractFromStringTemplateProperty(key, property);
        if (template) {
          candidates.push(template);
        }
      } else if (property.default !== undefined) {
        candidates.push(...extractFromArrayDefault(property.default));
      }

      if (property.enum || property.oneOf) {
        candidates.push(...extractFromTemplateProperty(property));
      }
    }
  }

  const seen = new Set<string>();
  return candidates.filter((template) => {
    if (seen.has(template.id)) {
      return false;
    }
    seen.add(template.id);
    return true;
  });
};
